import { useTranslation } from 'react-i18next';

import { Button, Tag } from '@arco-design/web-react';
import { IconDelete, IconEdit } from '@arco-design/web-react/icon';
import styled from 'styled-components';

import { VolumeOptionLabel } from '@/components/Workload/storage-settings';
import { MaasDataDiskMode } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { StorageClassDisplay, useResourceSectionReadOnly } from '@/delivery-type-plugin/components';

import type { MaasDataDiskCardItem } from './types';

interface MaasDataDiskItemsProps {
  item?: MaasDataDiskCardItem;
  onEdit?: () => void;
  onDelete?: () => void;
  isEdit?: boolean;
  hideDelete?: boolean;
}

export function MaasDataDiskItems({ item, onEdit, onDelete, isEdit, hideDelete }: MaasDataDiskItemsProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  if (!item) return null;

  const modeLabel = item.mode === MaasDataDiskMode.Create ? t('components:create.data.disk') : t('components:add.existing.data.disk');
  const isExisting = item.mode === MaasDataDiskMode.Existing;

  return (
    <ItemContainer>
      <DiskItemBox>
        <VolumeOptionLabel
          iconfontName="icon-yunpan"
          value={isExisting ? modeLabel : item.name}
          label={isExisting ? item.name : modeLabel}
          extraItems={[
            item.storageClassInfo || item.storageClass
              ? {
                  label: t('common:storage.class'),
                  value: item.storageClassInfo ? (
                    <StorageClassDisplay {...item.storageClassInfo} layout="stacked" />
                  ) : (
                    <StorageClassValue>
                      <span>{item.storageClass}</span>
                      {item.provisioner ? (
                        <Tag color="orange" size="small">
                          {item.provisioner}
                        </Tag>
                      ) : null}
                    </StorageClassValue>
                  ),
                  frozenWidth: 180,
                }
              : null,
            item.storageGi
              ? {
                  label: t('common:capacity'),
                  value: `${item.storageGi}Gi`,
                  frozenWidth: true,
                }
              : null,
            item.mountPath
              ? {
                  label: t('data-center:maas.model.mountPath'),
                  value: item.mountPath,
                  frozenWidth: 200,
                }
              : null,
          ].filter(Boolean)}
          style={{ flex: 1 }}
        />
        {!isEdit && !sectionReadOnly ? (
          <ItemActionBox>
            <Button iconOnly type="text" icon={<IconEdit />} onClick={onEdit} />
            {!hideDelete ? <Button iconOnly type="text" status="danger" icon={<IconDelete />} onClick={onDelete} /> : null}
          </ItemActionBox>
        ) : null}
      </DiskItemBox>
    </ItemContainer>
  );
}

const ItemContainer = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: var(--color-fill-1);
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-fill-2);
  }
`;

const DiskItemBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const StorageClassValue = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ItemActionBox = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
